import React, { useEffect, useRef, useState } from "react";
import { API_BASE, apiFetch } from "@/lib/api";
import HeaderBar from "@/components/HeaderBar";
import { fetchCoachEnabled } from "@/components/CoachFab";
import { PaperPlaneRight, ArrowCounterClockwise, Sparkle, Microphone, StopCircle, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";

const SESSION_KEY = "saproject:coach-session";

function newSessionId() {
  const id = `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(SESSION_KEY, id);
  return id;
}

const renderRich = (t) =>
  (t || "").split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? <b key={i}>{part.slice(2, -2)}</b> : part);

export default function CoachPage() {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || newSessionId());
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`${SESSION_KEY}:msgs`) || "[]"); } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sex, setSex] = useState(() => localStorage.getItem("saproject:coach-sex") || "");
  const [voiceReply, setVoiceReply] = useState(() => localStorage.getItem("saproject:coach-voice") === "1");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-grow the chat box line by line as the user types (max 10 lines, then scroll)
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = 10 * 20 + 26; // 10 lines × 20px line-height + vertical padding
    el.style.height = Math.min(el.scrollHeight, max) + "px";
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [input, transcribing, recording]);
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const [enabled, setEnabled] = useState(true);
  useEffect(() => { fetchCoachEnabled().then(setEnabled); }, []);

  useEffect(() => { document.title = "Coach — The Self-Agency Project"; }, []);
  useEffect(() => {
    localStorage.setItem(`${SESSION_KEY}:msgs`, JSON.stringify(messages.slice(-30)));
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const newChat = () => {
    setSessionId(newSessionId());
    setMessages([]);
    localStorage.removeItem(`${SESSION_KEY}:msgs`);
  };

  const pickSex = (v) => {
    const next = sex === v ? "" : v;
    setSex(next);
    localStorage.setItem("saproject:coach-sex", next);
  };

  const toggleVoice = () => {
    const next = !voiceReply;
    setVoiceReply(next);
    localStorage.setItem("saproject:coach-voice", next ? "1" : "0");
    if (!next && audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  };

  const speak = async (text) => {
    try {
      const res = await apiFetch(`${API_BASE}/coach/tts`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.slice(0, 3500) }),
      });
      if (!res.ok) return;
      const url = URL.createObjectURL(await res.blob());
      if (audioRef.current) audioRef.current.pause();
      audioRef.current = new Audio(url);
      audioRef.current.play().catch(() => {});
    } catch { /* voice is best-effort */ }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", new Blob(chunks, { type: rec.mimeType || "audio/webm" }), "say.webm");
          const res = await apiFetch(`${API_BASE}/coach/transcribe`, { method: "POST", body: fd });
          const d = await res.json().catch(() => ({}));
          if (res.ok && d.text) setInput((v) => (v ? `${v} ${d.text}` : d.text));
        } finally { setTranscribing(false); }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch { /* mic denied */ }
  };

  const stopRecording = () => {
    recRef.current?.stop();
    recRef.current = null;
    setRecording(false);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await apiFetch(`${API_BASE}/coach/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, sex }),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({})))?.detail || "Coach is unavailable right now — try again shortly.";
        setMessages((m) => m.map((msg, i) => (i === m.length - 1 ? { ...msg, text: detail } : msg)));
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        const snapshot = acc;
        setMessages((m) => m.map((msg, i) => (i === m.length - 1 ? { ...msg, text: snapshot } : msg)));
      }
      if (voiceReply && acc.trim()) speak(acc);
    } catch {
      setMessages((m) => m.map((msg, i) => (i === m.length - 1 && !msg.text
        ? { ...msg, text: "Connection hiccup — please try again." } : msg)));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0C0C0D] text-white flex flex-col">
      <HeaderBar />
      {!enabled ? (
        <div className="m-auto max-w-sm text-center text-sm text-[#A1A1AA] px-6" data-testid="coach-disabled">
          Coach isn't enabled for your school yet. Ask your teacher if you need someone to talk to —
          or call Kids Help Phone <b className="text-white">1-800-668-6868</b> any time.
        </div>
      ) : (
      <div className="flex-1 w-full max-w-3xl mx-auto px-4 pb-4 flex flex-col" data-testid="coach-page">
        <div className="flex items-center justify-between py-4">
          <div>
            <h1 className="font-display font-black text-2xl m-0 flex items-center gap-2">
              <Sparkle size={22} weight="duotone" color="#FFE01A" /> Coach
            </h1>
            <p className="text-xs text-[#A1A1AA] m-0 mt-1">
              Real-world advice grounded in your Self-Agency course. Coach isn't a counsellor, lawyer or emergency
              service — in a crisis call <b className="text-white">911</b> or Kids Help Phone <b className="text-white">1-800-668-6868</b>.
            </p>
          </div>
          <button onClick={newChat} data-testid="coach-new-session" title="Start a new conversation"
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-[#A1A1AA] hover:text-white bg-transparent border border-white/15 rounded-full px-3 py-1.5 cursor-pointer">
            <ArrowCounterClockwise size={13} /> New chat
          </button>
        </div>

        <div className="flex-1 overflow-y-auto rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-col gap-3 min-h-[40vh] mb-3" data-testid="coach-messages">
          {messages.length === 0 && (
            <div className="text-sm text-[#A1A1AA] m-auto text-center max-w-sm" data-testid="coach-empty">
              Tell Coach what you're dealing with — a conflict, a bully, a boundary someone keeps crossing,
              a situation that scares you — and get practical steps plus the course lessons that cover it.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} data-testid={`coach-msg-${m.role}`}
              className={m.role === "user"
                ? "self-end max-w-[85%] rounded-2xl rounded-br-sm bg-[#FFE01A] text-black text-sm px-4 py-2.5 whitespace-pre-wrap"
                : "self-start max-w-[85%] rounded-2xl rounded-bl-sm bg-white/[0.07] border border-white/10 text-sm px-4 py-2.5 whitespace-pre-wrap"}>
              {m.role === "assistant"
                ? (m.text ? renderRich(m.text) : (busy && i === messages.length - 1 ? "…" : ""))
                : m.text}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-[11px] text-[#71717A]">Optional — helps improve the course:</span>
          {[["male", "Guy"], ["female", "Girl"]].map(([v, label]) => (
            <button key={v} onClick={() => pickSex(v)} data-testid={`coach-sex-${v}`}
              className={`text-[11px] font-bold rounded-full px-3 py-1 border cursor-pointer ${
                sex === v ? "bg-[#FFE01A] text-black border-[#FFE01A]" : "bg-transparent text-[#A1A1AA] border-white/15"}`}>
              {label}
            </button>
          ))}
          <button onClick={toggleVoice} data-testid="coach-voice-toggle" title="Coach reads replies aloud"
            className={`ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold rounded-full px-3 py-1 border cursor-pointer ${
              voiceReply ? "bg-[#FFE01A] text-black border-[#FFE01A]" : "bg-transparent text-[#A1A1AA] border-white/15"}`}>
            {voiceReply ? <SpeakerHigh size={13} weight="bold" /> : <SpeakerSlash size={13} />}
            Voice replies {voiceReply ? "on" : "off"}
          </button>
        </div>

        <div className="mt-1 flex gap-2">
          <button onClick={recording ? stopRecording : startRecording} disabled={busy || transcribing}
            data-testid="coach-mic" title={recording ? "Stop and transcribe" : "Speak instead of typing"}
            className={`self-end inline-flex items-center justify-center rounded-xl border px-3.5 py-3 cursor-pointer disabled:opacity-40 ${
              recording ? "bg-red-500 border-red-500 text-white animate-pulse" : "bg-white/5 border-white/15 text-[#A1A1AA] hover:text-white"}`}>
            {recording ? <StopCircle size={18} weight="fill" /> : <Microphone size={18} />}
          </button>
          <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} rows={1} maxLength={1500}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={transcribing ? "Transcribing your voice…" : recording ? "Listening — tap stop when done" : "What's going on? (type, or tap the mic and speak)"} data-testid="coach-input"
            className="flex-1 rounded-xl bg-white/5 border border-white/15 text-white text-sm leading-5 px-4 py-3 placeholder:text-[#71717A] focus:outline-none focus:border-[#FFE01A] resize-none overflow-y-hidden" />
          <button onClick={send} disabled={busy || !input.trim()} data-testid="coach-send" title="Send"
            className="self-end inline-flex items-center justify-center rounded-xl border-0 px-3.5 py-3 bg-[#FFE01A] text-black cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
            <PaperPlaneRight size={18} weight="bold" />
          </button>
        </div>
        <p className="text-[10px] text-[#71717A] mt-2 m-0">
          Chats are summarized anonymously to improve the course, then deleted. No names or personal details are kept.
        </p>
      </div>
      )}
    </div>
  );
}
